// src/components/UserProfileMenu.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import api, { API_ORIGIN } from "../api/client";
import "../styles/userProfileMenu.css";

function resolveAvatarUrl(user) {
  const raw =
    user?.profileImageUrl ||
    user?.ProfileImageUrl ||
    user?.avatarUrl ||
    user?.ProfileImageURL;

  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;

  const cleaned = String(raw).trim().replaceAll("\\", "/");

  if (cleaned.startsWith("/storage/")) return `${API_ORIGIN}${cleaned}`;
  if (cleaned.toLowerCase().startsWith("storage/")) {
    return `${API_ORIGIN}/storage/${cleaned.substring("storage/".length)}`;
  }
  if (!cleaned.includes("/")) {
    return `${API_ORIGIN}/storage/ProfileImages/${cleaned}`;
  }

  return `${API_ORIGIN}/${cleaned.startsWith("/") ? cleaned.slice(1) : cleaned}`;
}

function Icon({ type }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
  };

  if (type === "camera") {
    return (
      <svg {...common}>
        <path d="M4 8a2 2 0 0 1 2-2h2l1.2-1.5h5.6L16 6h2a2 2 0 0 1 2 2v10H4V8Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <circle cx="12" cy="12.5" r="3.2" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }

  if (type === "security") {
    return (
      <svg {...common}>
        <path d="M12 3 19 6v5c0 4.6-2.9 8.1-7 10-4.1-1.9-7-5.4-7-10V6l7-3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="m9.5 12 1.6 1.6 3.7-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === "trash") {
    return (
      <svg {...common}>
        <path d="M5 7h14M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M10 5H5v14h5M14 8l4 4-4 4M18 12H9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function UserProfileMenu({ user, onLogout }) {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const fileInputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");

  const initials =
    user?.name
      ?.split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";

  const avatarSrc = useMemo(() => resolveAvatarUrl(user), [user]);

  useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function onKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const triggerFileSelect = () => {
    setMsg("");
    fileInputRef.current?.click();
  };

  async function handleFileChange(event) {
    setMsg("");
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMsg("Select a valid image file.");
      event.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setMsg("Image is too large. Maximum size is 2 MB.");
      event.target.value = "";
      return;
    }

    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      setProgress(0);

      await api.post("/profile/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          setProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        },
      });

      await refreshUser();
      setMsg("Profile photo updated.");
    } catch (error) {
      console.error("Upload failed", error);
      setMsg("Could not upload the photo.");
    } finally {
      setUploading(false);
      setProgress(0);
      URL.revokeObjectURL(localUrl);
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleRemoveImage() {
    setMsg("");

    try {
      setUploading(true);
      await api.delete("/profile/image");
      await refreshUser();
      setMsg("Profile photo removed.");
    } catch (error) {
      console.error("Remove failed", error);
      setMsg("Could not remove the photo.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="profile-menu" ref={rootRef}>
      <button
        className={`profile-trigger ${open ? "is-open" : ""}`}
        onClick={() => setOpen((value) => !value)}
        aria-label="Account menu"
        aria-expanded={open}
        type="button"
      >
        {preview ? (
          <img src={preview} alt="" />
        ) : avatarSrc ? (
          <img src={avatarSrc} alt="" />
        ) : (
          <span className="profile-initials" aria-hidden="true">
            {initials}
          </span>
        )}
      </button>

      {open ? (
        <div className="profile-dropdown" role="menu" aria-label="Account menu">
          <div className="profile-summary">
            <div className="profile-summary-avatar" aria-hidden="true">
              {avatarSrc ? <img src={avatarSrc} alt="" /> : <span>{initials}</span>}
            </div>
            <div className="profile-summary-copy">
              <strong>{user?.name || user?.Username || "User"}</strong>
              <span>{user?.email || user?.Email || ""}</span>
              <small>{user?.accountLabel || user?.institutionName || "LawAfrica account"}</small>
            </div>
          </div>

          {msg ? <div className="profile-msg">{msg}</div> : null}

          <div className="profile-actions">
            <button type="button" className="profile-action" onClick={triggerFileSelect} disabled={uploading}>
              <Icon type="camera" />
              <span>{uploading ? "Uploading…" : avatarSrc ? "Change photo" : "Upload photo"}</span>
            </button>

            {avatarSrc ? (
              <button type="button" className="profile-action" onClick={handleRemoveImage} disabled={uploading}>
                <Icon type="trash" />
                <span>Remove photo</span>
              </button>
            ) : null}

            <button
              type="button"
              className="profile-action"
              onClick={() => {
                setOpen(false);
                navigate("/dashboard/security");
              }}
            >
              <Icon type="security" />
              <span>Security</span>
            </button>

            <div className="profile-divider" />

            <button
              type="button"
              className="profile-action profile-action-signout"
              onClick={() => {
                setOpen(false);
                onLogout?.();
              }}
              disabled={uploading}
            >
              <Icon type="logout" />
              <span>Sign out</span>
            </button>
          </div>

          {uploading ? (
            <div className="profile-upload-progress" aria-label="Upload progress">
              <span style={{ width: `${progress}%` }} />
            </div>
          ) : null}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />
        </div>
      ) : null}
    </div>
  );
}
